const ARTIFACT_TYPES = new Set(['artifact', 'file', 'input_artifact']);
const STRING_TYPES = new Set([
  'date', 'datetime', 'email', 'enum', 'reference', 'select', 'selection',
  'string', 'text', 'textarea', 'time', 'url',
]);

function normalizedOptions(value) {
  const rawOptions = Array.isArray(value)
    ? value
    : String(value || '').split(/[\n,]/);
  return rawOptions.map((option) => {
    if (option && typeof option === 'object') {
      return option.value ?? option.key ?? option.label;
    }
    return String(option ?? '').trim();
  }).filter((option) => option !== '' && option !== null && typeof option !== 'undefined');
}

export function normalizedExecutionType(value) {
  const type = String(value || '').trim().toLowerCase();
  if (ARTIFACT_TYPES.has(type)) return 'artifact';
  if (STRING_TYPES.has(type)) return 'string';
  if (['integer', 'int'].includes(type)) return 'integer';
  if (['number', 'float', 'double', 'range'].includes(type)) return 'number';
  if (['boolean', 'bool', 'checkbox'].includes(type)) return 'boolean';
  return type;
}

export function capabilityInputsForProfiles(sharedFields = [], providerFields = []) {
  return [
    ...sharedFields.map((field) => ({
      ...field,
      source_category: 'shared_need_information',
      source_label: 'Shared Need Information',
    })),
    ...providerFields.map((field) => ({
      ...field,
      source_category: 'provider_specific_information',
      source_label: 'Provider-Specific Information',
    })),
  ].map((field) => ({
    ...field,
    field_type: field.field_type || field.schema_type || field.type || 'text',
    normalized_type: field.normalized_type || normalizedExecutionType(field.field_type || field.schema_type || field.type),
    is_artifact: field.is_artifact ?? normalizedExecutionType(field.field_type || field.schema_type || field.type) === 'artifact',
    options: normalizedOptions(field.options),
    accepted_content_types: normalizedOptions(field.accepted_content_types || field.accepted_file_types),
  }));
}

function commandParameters(command) {
  if (Array.isArray(command?.parameters)) return command.parameters;
  const schema = command?.parameters_schema || {};
  const properties = schema?.properties || {};
  const required = new Set(Array.isArray(schema?.required) ? schema.required : []);
  return Object.entries(properties).map(([key, property = {}]) => ({
    key,
    name: key,
    display_name: property.title || key.replaceAll('_', ' '),
    description: property.description || '',
    kind: 'parameter',
    type: property.type || 'string',
    field_type: property.type || 'string',
    normalized_type: normalizedExecutionType(property.type),
    required: required.has(key),
    options: Array.isArray(property.enum) ? property.enum : [],
    default: property.default,
  }));
}

function commandArtifactInputs(command) {
  if (Array.isArray(command?.artifact_inputs)) return command.artifact_inputs;
  const contract = command?.input_artifacts;
  if (!contract || typeof contract !== 'object') return [];
  if (contract.properties && typeof contract.properties === 'object') {
    const required = new Set(Array.isArray(contract.required) ? contract.required : []);
    return Object.entries(contract.properties).map(([key, property = {}]) => ({
      key,
      name: key,
      display_name: property.title || key.replaceAll('_', ' '),
      description: property.description || '',
      kind: 'input_artifact',
      type: 'input_artifact',
      field_type: 'input_artifact',
      normalized_type: 'artifact',
      required: required.has(key),
      accepted_media_types: property.accepted_media_types || [],
    }));
  }
  const key = contract.key || 'input_artifacts';
  return [{
    key,
    name: key,
    display_name: contract.title || contract.display_name || 'Input Artifact',
    description: contract.description || 'File or artifact supplied to this command.',
    kind: 'input_artifact',
    type: 'input_artifact',
    field_type: 'input_artifact',
    normalized_type: 'artifact',
    required: Boolean(contract.required) || Number(contract.min_items || 0) > 0,
    accepted_media_types: contract.accepted_media_types || [],
    min_items: contract.min_items,
    max_items: contract.max_items,
  }];
}

export function normalizeAdvertisedCommand(command = {}) {
  const parameters = commandParameters(command).map((input) => ({
    ...input,
    normalized_type: input.normalized_type || normalizedExecutionType(input.field_type || input.type),
  }));
  const artifactInputs = commandArtifactInputs(command).map((input) => ({
    ...input,
    normalized_type: 'artifact',
  }));
  return {
    ...command,
    command_identifier: command.command_identifier || command.command_key || command.operation,
    command_key: command.command_key || command.operation,
    parameters,
    artifact_inputs: artifactInputs,
    inputs: [...parameters, ...artifactInputs],
  };
}

export function executionTypesCompatible(source, target) {
  const sourceType = source.normalized_type || normalizedExecutionType(source.field_type || source.type);
  const targetType = target.normalized_type || normalizedExecutionType(target.field_type || target.type);
  if (!(sourceType === targetType || (sourceType === 'integer' && targetType === 'number'))) {
    return false;
  }
  if (sourceType === 'artifact' && source.multiple && Number(target.max_items) === 1) {
    return false;
  }
  const sourceOptions = Array.isArray(source.options) ? source.options : [];
  const targetOptions = Array.isArray(target.options) ? target.options : [];
  return !(sourceOptions.length && targetOptions.length)
    || sourceOptions.every((option) => targetOptions.includes(option));
}

export function compatibleCommandTargets(field, command) {
  const normalizedCommand = normalizeAdvertisedCommand(command);
  const targets = field.is_artifact
    ? normalizedCommand.artifact_inputs
    : normalizedCommand.parameters;
  return targets.filter((target) => executionTypesCompatible(field, target));
}

export function mappingsByCapabilityInput(profile = {}) {
  const result = {};
  (profile.parameter_mappings || []).forEach((mapping) => {
    result[mapping.capability_input_key] = {
      kind: 'parameter',
      targetKey: mapping.command_parameter_key,
    };
  });
  (profile.artifact_mappings || []).forEach((mapping) => {
    result[mapping.capability_input_key] = {
      kind: 'artifact',
      targetKey: mapping.command_artifact_key,
    };
  });
  return result;
}

export function executionProfilePayload(selectedCommandIdentifier, mappings = {}) {
  const parameterMappings = [];
  const artifactMappings = [];
  Object.entries(mappings).forEach(([sourceKey, mapping]) => {
    if (!mapping?.targetKey) return;
    if (mapping.kind === 'artifact') {
      artifactMappings.push({
        capability_input_key: sourceKey,
        command_artifact_key: mapping.targetKey,
      });
    } else {
      parameterMappings.push({
        capability_input_key: sourceKey,
        command_parameter_key: mapping.targetKey,
      });
    }
  });
  return {
    selected_command_identifier: selectedCommandIdentifier || null,
    parameter_mappings: parameterMappings,
    artifact_mappings: artifactMappings,
  };
}

export function localExecutionProfileStatus(profile, command, capabilityInputs = []) {
  if (!profile) return { status: 'not_configured', is_executable: false, missing_required_inputs: [] };
  if (!command) return { status: 'requires_attention', is_executable: false, missing_required_inputs: [] };
  const normalizedCommand = normalizeAdvertisedCommand(command);
  const fieldsByKey = Object.fromEntries(capabilityInputs.map((field) => [field.key, field]));
  const seenTargets = new Set();
  const invalidMappings = capabilityInputs.length ? Object.entries(mappingsByCapabilityInput(profile)).filter(
    ([sourceKey, mapping]) => {
      const source = fieldsByKey[sourceKey];
      const targetIdentity = `${mapping.kind}:${mapping.targetKey}`;
      const duplicate = seenTargets.has(targetIdentity);
      seenTargets.add(targetIdentity);
      return !source || duplicate || !compatibleCommandTargets(source, normalizedCommand)
        .some((target) => target.key === mapping.targetKey);
    },
  ) : [];
  if (invalidMappings.length) {
    return {
      status: 'requires_attention',
      is_executable: false,
      missing_required_inputs: [],
      invalid_mappings: invalidMappings,
    };
  }
  const mappedTargets = new Set([
    ...(profile.parameter_mappings || []).map((mapping) => `parameter:${mapping.command_parameter_key}`),
    ...(profile.artifact_mappings || []).map((mapping) => `artifact:${mapping.command_artifact_key}`),
  ]);
  const missing = normalizedCommand.inputs.filter((input) => input.required && !mappedTargets.has(
    `${input.kind === 'parameter' ? 'parameter' : 'artifact'}:${input.key}`,
  ));
  return {
    status: missing.length ? 'incomplete' : 'ready',
    is_executable: missing.length === 0,
    missing_required_inputs: missing,
  };
}
